// pages/RegisterPage.js
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ErrorDisplay from '../components/ErrorDisplay'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import { usePasswordValidation } from '../hooks/usePasswordValidation'
import { getAppName } from '../utils/domainUtils'
import PasswordRequirements from '../components/PasswordRequirements'
import DynamicMetaTags from '../components/DynamicMetaTags'
import './Auth.css'

function RegisterPage () {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    inviteCode: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [validatedCodeData, setValidatedCodeData] = useState(null) // { companyId, companyName, role }
  const [codeError, setCodeError] = useState(null)

  const { register, error, setError } = useAuth()
  const navigate = useNavigate()
  const functions = getFunctions(app)

  // Password validation hook
  const {
    passwordStrength,
    updatePasswordStrength,
    validatePassword,
    passwordsMatch
  } = usePasswordValidation()

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Live password strength validation
    if (name === 'password') {
      updatePasswordStrength(value)
    }

    // Reset validation when invite code changes
    if (name === 'inviteCode') {
      setValidatedCodeData(null)
      setCodeError(null)
    }
  }

  // Validate invite code
  const handleValidateCode = async () => {
    if (!formData.inviteCode || formData.inviteCode.trim().length === 0) {
      setCodeError('Bitte geben Sie einen Einladungscode ein')
      return
    }

    setIsValidatingCode(true)
    setCodeError(null)

    try {
      const validateInviteCode = httpsCallable(functions, 'validateInviteCode')
      const result = await validateInviteCode({ code: formData.inviteCode.trim() })

      if (result.data.valid) {
        setValidatedCodeData({
          companyId: result.data.companyId,
          companyName: result.data.companyName,
          role: result.data.role
        })
        setCodeError(null)
      } else {
        setCodeError(result.data.message || 'Ungültiger Einladungscode')
        setValidatedCodeData(null)
      }
    } catch (error) {
      console.error('Error validating invite code:', error)
      setCodeError('Fehler bei der Validierung. Bitte versuchen Sie es erneut.')
      setValidatedCodeData(null)
    } finally {
      setIsValidatingCode(false)
    }
  }

  const validateForm = () => {
    if (!formData.inviteCode || !validatedCodeData) {
      setError('Bitte validieren Sie zuerst Ihren Einladungscode.')
      return false
    }

    if (!formData.email || !formData.password || !formData.displayName) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.')
      return false
    }

    // Strenge Passwort-Validierung
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0])
      return false
    }

    if (!passwordsMatch(formData.password, formData.confirmPassword)) {
      setError('Die Passwörter stimmen nicht überein.')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein.')
      return false
    }

    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const additionalData = {
        preferences: {
          notifications: true,
          theme: 'light'
        }
      }

      // Register user
      const user = await register(
        formData.email,
        formData.password,
        formData.displayName,
        additionalData
      )

      // Mark invite code as used
      const useInviteCode = httpsCallable(functions, 'useInviteCode')
      await useInviteCode({
        code: formData.inviteCode.trim(),
        userId: user.uid,
        userEmail: formData.email
      })

      navigate('/search')
    } catch (error) {
      // Fehler wird bereits im AuthContext behandelt
      console.error('Registrierung fehlgeschlagen:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='auth-container'>
      <DynamicMetaTags 
        pageTitle="Registrieren"
        description="Erstellen Sie ein neues Konto für die KI-gestützte Wissensdatenbank."
      />
      
      <div className='auth-card'>
        <h1>{getAppName()}</h1>
        <h2>Konto erstellen</h2>
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.7)', 
          marginBottom: '2rem', 
          fontSize: '0.95rem',
          marginTop: '-1rem'
        }}>
          Erstellen Sie Ihr Konto, um loszulegen
        </p>

        <ErrorDisplay
          error={error}
          onDismiss={() => setError(null)}
        />

        <form onSubmit={handleSubmit} className='auth-form'>
          {/* Invite Code Section */}
          <div className='auth-form-group'>
            <label htmlFor='inviteCode'>
              Einladungscode <span className='required'>*</span>
            </label>
            <div className='invite-code-validation'>
              <input
                type='text'
                id='inviteCode'
                name='inviteCode'
                value={formData.inviteCode}
                onChange={handleChange}
                placeholder='z.B. ASCLEPION-2024-ABC123'
                disabled={isLoading || isValidatingCode || validatedCodeData}
                required
              />
              <button
                type='button'
                onClick={handleValidateCode}
                disabled={
                  isLoading ||
                  isValidatingCode ||
                  !formData.inviteCode ||
                  validatedCodeData
                }
                className='btn-secondary'
              >
                {isValidatingCode ? 'Prüfe...' : validatedCodeData ? '✓ Gültig' : 'Validieren'}
              </button>
            </div>
            {codeError && (
              <div className='error-message'>
                {codeError}
              </div>
            )}
            {validatedCodeData && (
              <div className='success-message'>
                <span>✓</span>
                <span>Sie registrieren sich für: {validatedCodeData.companyName}</span>
              </div>
            )}
          </div>

          <div className='auth-form-group'>
            <label htmlFor='displayName'>
              Name <span className='required'>*</span>
            </label>
            <input
              type='text'
              id='displayName'
              name='displayName'
              value={formData.displayName}
              onChange={handleChange}
              placeholder='Ihr vollständiger Name'
              disabled={isLoading || !validatedCodeData}
              required
            />
          </div>

          <div className='auth-form-group'>
            <label htmlFor='email'>
              E-Mail <span className='required'>*</span>
            </label>
            <input
              type='email'
              id='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='ihre.email@beispiel.de'
              disabled={isLoading || !validatedCodeData}
              required
            />
          </div>

          <div className='auth-form-group'>
            <label htmlFor='password'>
              Passwort <span className='required'>*</span>
            </label>
            <input
              type='password'
              id='password'
              name='password'
              value={formData.password}
              onChange={handleChange}
              placeholder='Sicheres Passwort erstellen'
              disabled={isLoading || !validatedCodeData}
              required
            />
            {formData.password && (
              <PasswordRequirements passwordStrength={passwordStrength} />
            )}
          </div>

          <div className='auth-form-group'>
            <label htmlFor='confirmPassword'>
              Passwort bestätigen <span className='required'>*</span>
            </label>
            <input
              type='password'
              id='confirmPassword'
              name='confirmPassword'
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder='Passwort wiederholen'
              disabled={isLoading || !validatedCodeData}
              required
            />
          </div>

          <button
            type='submit'
            className='btn-primary auth-btn'
            disabled={isLoading || !validatedCodeData}
          >
            {isLoading ? 'Registrierung läuft...' : 'Registrieren'}
          </button>
        </form>

        <div className='auth-links'>
          <p>
            Bereits ein Konto?
            <Link to='/login' className='auth-link'>
              Hier anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
